/**
 * How principal-diagnosis fitness is *shown*. The judgment itself is the
 * backend's (`primary_diagnosis_fitness.py`); this file only decides how loudly
 * to say it, so all four surfaces — the OASIS Active Diagnosis page, the coding
 * page, the claim worksheet, and the diagnoses report — say it the same way.
 *
 * Two tiers, one consequence. `barred` and `imprecise` both mean Medicare
 * cannot group the claim; they differ in what the coder should do next. That is
 * why `imprecise` looks calmer but is never treated as safe to accept.
 */
import type { PrimaryDiagnosisFitness } from '../services/api/types.gen';

/**
 * Whether this code, in the primary seat, warrants pushing back at all.
 *
 * Absent fitness counts as discouraged. The backend already refuses to call an
 * unlisted code fit — `evaluate_primary_fitness(found=False)` returns an unfit
 * `unknown` — so reading a missing value as "fine" would make the two ends of
 * the stack fail in opposite directions. In practice absence means the lookup
 * has not resolved yet or the request failed, and neither is evidence the code
 * is safe to seat.
 */
export function shouldDiscourage(
  fitness: PrimaryDiagnosisFitness | null | undefined
): boolean {
  return fitness == null || !fitness.fit;
}

/**
 * Whether the gate must *wait* instead of judging.
 *
 * `shouldDiscourage` is deliberately fail-closed, which is right when absence
 * means the lookup failed and wrong when it merely has not landed yet. Both
 * look identical in the map, so callers pass the hook's `fitnessPending`: while
 * a request is in flight an absent entry is not evidence of anything.
 *
 * Waiting, not warning — and never proceeding. Reading an unresolved code as
 * fit would seat it with no confirm at all, which is the failure this feature
 * exists to prevent; reading it as unfit tells the coder we checked when we did
 * not, and a warning that cries wolf is spent by the time it is true.
 */
export function fitnessUnresolved(
  fitness: PrimaryDiagnosisFitness | null | undefined,
  pending: boolean
): boolean {
  return pending && fitness == null;
}

/**
 * Whether the warning should be a full-strength one.
 *
 * `payerKnown` is deliberately separate from `groupsViaPdgm`. An episode with
 * no insurance on file yet — routine at OASIS time — is not a non-PDGM episode;
 * that population is the most likely to end up Medicare, so it gets the full
 * warning rather than the softened note.
 */
export function isPdgmRelevant(context: {
  groupsViaPdgm: boolean;
  payerKnown: boolean;
}): boolean {
  return context.groupsViaPdgm || !context.payerKnown;
}

export interface FitnessPresentation {
  /** Short label for a badge or chip. */
  label: string;
  /** The sentence shown to the clinician. */
  message: string;
  /** Tailwind classes for an inline warning line. */
  toneClass: string;
  /** Tailwind classes for the badge/dot in a search result row. */
  markClass: string;
}

const BARRED: Omit<FitnessPresentation, 'message'> = {
  label: 'Not a PDGM primary',
  toneClass: 'text-destructive',
  markClass: 'text-destructive',
};

// Calmer than barred because the fix is usually thirty seconds away — not
// because the claim fares any better.
const IMPRECISE: Omit<FitnessPresentation, 'message'> = {
  label: 'Too unspecific',
  toneClass: 'text-amber-700',
  markClass: 'text-amber-600',
};

// Covers both "the grouper does not list this code" and "this code is in the
// grouper but is not billable", so the label cannot claim either specifically —
// the explanation sentence carries the distinction.
const UNKNOWN: Omit<FitnessPresentation, 'message'> = {
  label: 'Cannot be primary',
  toneClass: 'text-destructive',
  markClass: 'text-destructive',
};

/**
 * Presentation for an unfit code — null when the code is fit, and null when
 * fitness is simply unknown yet.
 *
 * Absent fitness renders nothing rather than a warning: the lookup may still be
 * in flight, and flashing "cannot group" at a code we have not checked would
 * cry wolf. The *gate* still treats absence as discouraged (`shouldDiscourage`),
 * so nothing gets seated unchallenged; only the standing annotation waits.
 *
 * `softened` is for payers that do not group through PDGM — the edge-case
 * insurances that accept these codes. It leads with the payer, because a
 * sentence that opens "Medicare cannot group this claim" and then says Medicare
 * is not the payer reads as a bug rather than as reassurance.
 */
export function fitnessPresentation(
  fitness: PrimaryDiagnosisFitness | null | undefined,
  options: { softened?: boolean } = {}
): FitnessPresentation | null {
  if (!fitness || fitness.fit) return null;

  const base =
    fitness.tier === 'imprecise'
      ? IMPRECISE
      : fitness.tier === 'unknown'
        ? UNKNOWN
        : BARRED;

  const message =
    fitness.explanation ?? 'This code cannot serve as the primary diagnosis.';

  if (options.softened) {
    return {
      ...base,
      label: 'Not a PDGM primary',
      message: `This payer does not group through PDGM, so this code is billable here. Medicare would not accept it.`,
      toneClass: 'text-muted-foreground',
      markClass: 'text-muted-foreground',
    };
  }

  return { ...base, message };
}
